import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { fetchOrderDetailsByOrderId } from "../../shared/commerceMastersApi";
import { DEFAULT_ORDER_ITEM_STATUS, normalizeOrderItemStatus, ORDER_ITEM_STATUSES } from "../../shared/orderItemStatuses";
import { DEFAULT_PAYMENT_STATUS, normalizePaymentStatus, PAYMENT_STATUSES } from "../../shared/paymentStatuses";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  date: "",
  customerId: "",
  orderStatusId: 0,
  paymentStatus: DEFAULT_PAYMENT_STATUS,
  orderNo: "",
  paymentMethod: "",
  transactionId: "",
  subTotal: 0,
  discount: 0,
  taxable: 0,
  taxAmount: 0,
  deliveryCharges: 0,
  grandTotal: 0,
  customerAddressId: "",
  remarks: "",
  deliveryDate: ""
});

const emptyDetailForm = () => ({
  productId: "",
  vendorId: "",
  vendorAddressId: "",
  productVarientId: "",
  unitPrice: 0,
  discount: 0,
  taxable: 0,
  taxAmount: 0,
  grandTotal: 0,
  itemStatus: DEFAULT_ORDER_ITEM_STATUS
});

function normalizeOrderDetail(row) {
  if (!row) return null;
  return {
    id: row.id ?? row.Id,
    orderId: row.orderId ?? row.OrderId,
    productId: row.productId ?? row.ProductId,
    vendorId: row.vendorId ?? row.VendorId,
    vendorAddressId: row.vendorAddressId ?? row.VendorAddressId,
    productVarientId: row.productVarientId ?? row.ProductVarientId,
    unitPrice: row.unitPrice ?? row.UnitPrice ?? 0,
    discount: row.discount ?? row.Discount ?? 0,
    taxable: row.taxable ?? row.Taxable ?? 0,
    taxAmount: row.taxAmount ?? row.TaxAmount ?? 0,
    grandTotal: row.grandTotal ?? row.GrandTotal ?? 0,
    itemStatus: normalizeOrderItemStatus(row.itemStatus ?? row.ItemStatus)
  };
}

function buildOrderDetailPayload(line) {
  return {
    id: line.id ?? 0,
    orderId: line.orderId,
    productId: Number(line.productId),
    vendorId: Number(line.vendorId),
    vendorAddressId: Number(line.vendorAddressId),
    productVarientId: Number(line.productVarientId),
    unitPrice: Number(line.unitPrice || 0),
    discount: Number(line.discount || 0),
    taxable: Number(line.taxable || 0),
    taxAmount: Number(line.taxAmount || 0),
    grandTotal: Number(line.grandTotal || 0),
    itemStatus: normalizeOrderItemStatus(line.itemStatus)
  };
}

function asInputDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function rowToForm(x) {
  return {
    date: asInputDateTime(x.date),
    customerId: x.customerId ?? "",
    orderStatusId: x.orderStatusId ?? 0,
    paymentStatus: normalizePaymentStatus(x.paymentStatus),
    orderNo: x.orderNo ?? "",
    paymentMethod: x.paymentMethod ?? "",
    transactionId: x.transactionId ?? "",
    subTotal: x.subTotal ?? 0,
    discount: x.discount ?? 0,
    taxable: x.taxable ?? 0,
    taxAmount: x.taxAmount ?? 0,
    deliveryCharges: x.deliveryCharges ?? 0,
    grandTotal: x.grandTotal ?? 0,
    customerAddressId: x.customerAddressId ?? "",
    remarks: x.remarks ?? "",
    deliveryDate: asInputDateTime(x.deliveryDate)
  };
}

function mapDetailDtoToForm(x) {
  const line = normalizeOrderDetail(x);
  return {
    productId: line.productId ?? "",
    vendorId: line.vendorId ?? "",
    vendorAddressId: line.vendorAddressId ?? "",
    productVarientId: line.productVarientId ?? "",
    unitPrice: line.unitPrice ?? 0,
    discount: line.discount ?? 0,
    taxable: line.taxable ?? 0,
    taxAmount: line.taxAmount ?? 0,
    grandTotal: line.grandTotal ?? 0,
    itemStatus: line.itemStatus
  };
}

/** Same idea as pickPrimaryAddress — order lines have no default flag; use first row. */
function pickPrimaryLine(list) {
  if (!list?.length) return null;
  return list[0];
}

function detailFormHasIntent(df, detailEditingId) {
  if (detailEditingId) return true;
  return Boolean(
    df.productId ||
      df.vendorId ||
      df.vendorAddressId ||
      df.productVarientId ||
      Number(df.unitPrice) !== 0 ||
      Number(df.discount) !== 0 ||
      Number(df.taxable) !== 0 ||
      Number(df.taxAmount) !== 0 ||
      Number(df.grandTotal) !== 0
  );
}

export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editParam = searchParams.get("edit");
  const detailEditParam = searchParams.get("detailEdit");
  const skipDetailHydrateRef = useRef(false);

  useEffect(() => {
    const id = editParam != null ? Number(editParam) : NaN;
    if (!editParam || !Number.isFinite(id)) {
      navigate("/orders-list", { replace: true });
    }
  }, [editParam, navigate]);

  const [customers, setCustomers] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorAddresses, setVendorAddresses] = useState([]);
  const [productVarients, setProductVarients] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());

  const [detailEditingId, setDetailEditingId] = useState(null);
  const [detailForm, setDetailForm] = useState(() => emptyDetailForm());
  const [orderLines, setOrderLines] = useState([]);
  const [statusSavingId, setStatusSavingId] = useState(null);

  const activeTab = searchParams.get("tab") === "details" ? "details" : "order";

  const addressesForCustomer = customerAddresses.filter(
    (a) => !form.customerId || Number(a.customerId) === Number(form.customerId)
  );

  const vendorAddressesForVendor = vendorAddresses.filter(
    (a) => !detailForm.vendorId || Number(a.vendorId) === Number(detailForm.vendorId)
  );

  const varientsForProduct = productVarients.filter(
    (v) => !detailForm.productId || Number(v.productId) === Number(detailForm.productId)
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [cRes, caRes, pRes, vRes, vaRes, pvRes] = await Promise.all([
        apiGet("/api/Customers"),
        apiGet("/api/CustomerAddresses"),
        apiGet("/api/Products"),
        apiGet("/api/Vendors"),
        apiGet("/api/VendorAddresses"),
        apiGet("/api/ProductVarients")
      ]);
      if (cancelled) return;
      if (cRes.ok) setCustomers(cRes.data ?? []);
      if (caRes.ok) setCustomerAddresses(caRes.data ?? []);
      if (pRes.ok) setProducts(pRes.data ?? []);
      if (vRes.ok) setVendors(vRes.data ?? []);
      if (vaRes.ok) setVendorAddresses(vaRes.data ?? []);
      if (pvRes.ok) setProductVarients(pvRes.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editParam && searchParams.get("detailEdit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("detailEdit");
      setSearchParams(next, { replace: true });
    }
  }, [editParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      setDetailEditingId(null);
      setDetailForm(emptyDetailForm());
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const res = await apiGet(`/api/Orders/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load order");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm(rowToForm(x));
      const keepDetailDraft = new URLSearchParams(window.location.search).get("detailEdit");
      if (!keepDetailDraft) {
        skipDetailHydrateRef.current = false;
        setDetailEditingId(null);
        setDetailForm(emptyDetailForm());
      }
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const loadOrderLines = async (orderId) => {
    const { ok, items, error: err } = await fetchOrderDetailsByOrderId(orderId);
    if (!ok) {
      setDetailError(err ?? "Failed to load order items");
      setOrderLines([]);
      return [];
    }
    const normalized = items.map(normalizeOrderDetail).filter(Boolean);
    setOrderLines(normalized);
    return normalized;
  };

  useEffect(() => {
    if (!editingId) {
      setOrderLines([]);
      return;
    }
    void loadOrderLines(editingId);
  }, [editingId]);

  useEffect(() => {
    if (!detailEditParam || activeTab !== "details" || !editingId) {
      return;
    }
    const id = Number(detailEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/OrderDetails/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setDetailError(res.error ?? "Failed to load order detail");
        return;
      }
      const row = res.data;
      if (Number(row.orderId) !== Number(editingId)) {
        setDetailError("Order detail does not belong to this order.");
        return;
      }
      setDetailError(null);
      setDetailEditingId(row.id);
      setDetailForm(mapDetailDtoToForm(row));
    })();
    return () => {
      cancelled = true;
    };
  }, [detailEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "details" || detailEditParam) {
      return;
    }
    if (skipDetailHydrateRef.current) {
      skipDetailHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const items = await loadOrderLines(editingId);
      if (cancelled) return;
      setDetailError(null);
      const pick = pickPrimaryLine(items);
      if (!pick) {
        setDetailEditingId(null);
        setDetailForm(emptyDetailForm());
        return;
      }
      setDetailEditingId(pick.id);
      setDetailForm(mapDetailDtoToForm(pick));
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, detailEditParam]);

  const selectTab = (tab) => {
    if (tab === "order") {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      next.delete("detailEdit");
      setSearchParams(next, { replace: true });
      setDetailError(null);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", "details");
    next.delete("detailEdit");
    setSearchParams(next, { replace: true });
    setDetailError(null);
  };

  const updateLineItemStatus = async (line, newStatus) => {
    const normalizedStatus = normalizeOrderItemStatus(newStatus);
    setStatusSavingId(line.id);
    setDetailError(null);
    const res = await apiPost("/api/OrderDetails", buildOrderDetailPayload({ ...line, itemStatus: normalizedStatus }));
    setStatusSavingId(null);
    if (!res.ok) {
      setDetailError(res.error ?? "Failed to update item status");
      return;
    }
    setOrderLines((prev) => prev.map((x) => (x.id === line.id ? { ...x, itemStatus: normalizedStatus } : x)));
    if (Number(detailEditingId) === Number(line.id)) {
      setDetailForm((prev) => ({ ...prev, itemStatus: normalizedStatus }));
    }
  };

  const renderOrderItemsPanel = () => {
    if (!editingId) return null;
    return (
      <section className="order-items-status-panel">
        <div className="order-items-status-panel__head">
          <h3>Order items</h3>
          <p>Set the delivery status for each product in this order.</p>
        </div>
        {orderLines.length === 0 ? (
          <p className="order-items-status-panel__empty">No order items yet.</p>
        ) : (
          <div className="order-items-status-panel__list">
            {orderLines.map((line) => {
              const product = products.find((p) => Number(p.id) === Number(line.productId));
              const productName = product?.name || `Product #${line.productId}`;
              return (
                <div key={line.id} className="order-items-status-panel__row">
                  <div className="order-items-status-panel__product">
                    <strong>{productName}</strong>
                    <span>Line #{line.id}</span>
                  </div>
                  <label className="order-items-status-panel__status-field">
                    <span>Item status</span>
                    <select
                      value={line.itemStatus}
                      disabled={statusSavingId === line.id || saving}
                      onChange={(e) => void updateLineItemStatus(line, e.target.value)}
                    >
                      {ORDER_ITEM_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const saveAll = async () => {
    if (!form.customerId || !form.customerAddressId) {
      setError("Customer and customer address are required.");
      return;
    }

    const dIntent = detailFormHasIntent(detailForm, detailEditingId);
    if (dIntent) {
      if (!detailForm.productId || !detailForm.vendorId || !detailForm.vendorAddressId || !detailForm.productVarientId) {
        setError(null);
        setDetailError("Order detail: select product, variant, vendor, and vendor address—or clear the line fields.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setDetailError(null);

    const priorOrderId = editingId;
    const deliveryIso = form.deliveryDate ? new Date(form.deliveryDate).toISOString() : null;

    const orderRes = await apiPost("/api/Orders", {
      id: priorOrderId ?? 0,
      date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
      customerId: Number(form.customerId),
      orderStatusId: Number(form.orderStatusId || 0),
      paymentStatus: normalizePaymentStatus(form.paymentStatus),
      orderNo: form.orderNo || null,
      paymentMethod: form.paymentMethod || null,
      transactionId: form.transactionId || null,
      subTotal: Number(form.subTotal || 0),
      discount: Number(form.discount || 0),
      taxable: Number(form.taxable || 0),
      taxAmount: Number(form.taxAmount || 0),
      deliveryCharges: Number(form.deliveryCharges || 0),
      grandTotal: Number(form.grandTotal || 0),
      customerAddressId: Number(form.customerAddressId),
      remarks: form.remarks || null,
      deliveryDate: deliveryIso
    });

    if (!orderRes.ok) {
      setError(orderRes.error ?? "Failed to save order");
      setSaving(false);
      return;
    }

    const orderId = priorOrderId ?? orderRes.data?.id;
    if (!orderId) {
      setError("Order was saved but no id was returned.");
      setSaving(false);
      return;
    }

    if (dIntent) {
      const detailRes = await apiPost(
        "/api/OrderDetails",
        buildOrderDetailPayload({
          id: detailEditingId ?? 0,
          orderId,
          productId: detailForm.productId,
          vendorId: detailForm.vendorId,
          vendorAddressId: detailForm.vendorAddressId,
          productVarientId: detailForm.productVarientId,
          unitPrice: detailForm.unitPrice,
          discount: detailForm.discount,
          taxable: detailForm.taxable,
          taxAmount: detailForm.taxAmount,
          grandTotal: detailForm.grandTotal,
          itemStatus: detailForm.itemStatus
        })
      );
      if (!detailRes.ok) {
        setDetailError(detailRes.error ?? "Failed to save order detail");
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    navigate("/orders-list");
  };

  const onRefreshOrder = async () => {
    setError(null);
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/Orders/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load order");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm(rowToForm(x));
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  const onRefreshClick = async () => {
    if (activeTab === "details") {
      setDetailError(null);
      if (!editingId) return;
      const did = searchParams.get("detailEdit");
      if (did && editingId) {
        const res = await apiGet(`/api/OrderDetails/${Number(did)}`);
        if (res.ok && Number(res.data.orderId) === Number(editingId)) {
          const row = res.data;
          setDetailEditingId(row.id);
          setDetailForm(mapDetailDtoToForm(row));
        }
      } else {
        const items = await loadOrderLines(editingId);
        const pick = pickPrimaryLine(items);
        if (!pick) {
          setDetailEditingId(null);
          setDetailForm(emptyDetailForm());
        } else {
          setDetailEditingId(pick.id);
          setDetailForm(mapDetailDtoToForm(pick));
        }
      }
    } else {
      await onRefreshOrder();
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Edit Order</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void saveAll()}
            disabled={saving || loading}
            title="Save order and order detail"
            aria-label="Save"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefreshClick()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/orders-list" className="icon-button icon-button--toolbar" title="Orders list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>

      <div className="card-body">
        <div className="form-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "order" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "order"}
            onClick={() => selectTab("order")}
          >
            Order
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "details" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "details"}
            onClick={() => selectTab("details")}
          >
            Order details
          </button>
        </div>

        {activeTab === "order" && (
          <form
            id="orders-form"
            className="form form-fields-bottomline"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAll();
            }}
          >
            {editingId != null && (
              <label className="field">
                <span>Order ID</span>
                <input readOnly value={String(editingId)} />
              </label>
            )}
            <label className="field">
              <span>Date</span>
              <input type="datetime-local" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
            </label>
            <label className="field">
              <span>Customer</span>
              <select
                value={form.customerId}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : "";
                  setForm((p) => {
                    const next = { ...p, customerId: v };
                    if (v && p.customerAddressId) {
                      const ok = customerAddresses.some(
                        (ca) => Number(ca.id) === Number(p.customerAddressId) && Number(ca.customerId) === v
                      );
                      if (!ok) next.customerAddressId = "";
                    }
                    if (!v) next.customerAddressId = "";
                    return next;
                  });
                }}
              >
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || `Customer #${c.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Customer Address</span>
              <select
                value={form.customerAddressId}
                onChange={(e) => setForm((p) => ({ ...p, customerAddressId: e.target.value ? Number(e.target.value) : "" }))}
                disabled={!form.customerId}
              >
                <option value="">{form.customerId ? "Select address" : "Select customer first"}</option>
                {addressesForCustomer.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.address ?? `Address #${c.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Order Status Id</span>
              <input type="number" value={form.orderStatusId} onChange={(e) => setForm((p) => ({ ...p, orderStatusId: e.target.value }))} />
            </label>
            <label className="field">
              <span>Payment Status</span>
              <select value={form.paymentStatus} onChange={(e) => setForm((p) => ({ ...p, paymentStatus: e.target.value }))}>
                {PAYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Order No</span>
              <input value={form.orderNo} onChange={(e) => setForm((p) => ({ ...p, orderNo: e.target.value }))} />
            </label>
            <label className="field">
              <span>Payment Method</span>
              <input value={form.paymentMethod} onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value }))} />
            </label>
            <label className="field">
              <span>Transaction Id</span>
              <input value={form.transactionId} onChange={(e) => setForm((p) => ({ ...p, transactionId: e.target.value }))} />
            </label>
            <label className="field">
              <span>Sub Total</span>
              <input type="number" value={form.subTotal} onChange={(e) => setForm((p) => ({ ...p, subTotal: e.target.value }))} />
            </label>
            <label className="field">
              <span>Discount</span>
              <input type="number" value={form.discount} onChange={(e) => setForm((p) => ({ ...p, discount: e.target.value }))} />
            </label>
            <label className="field">
              <span>Taxable</span>
              <input type="number" value={form.taxable} onChange={(e) => setForm((p) => ({ ...p, taxable: e.target.value }))} />
            </label>
            <label className="field">
              <span>Tax Amount</span>
              <input type="number" value={form.taxAmount} onChange={(e) => setForm((p) => ({ ...p, taxAmount: e.target.value }))} />
            </label>
            <label className="field">
              <span>Delivery Charges</span>
              <input type="number" value={form.deliveryCharges} onChange={(e) => setForm((p) => ({ ...p, deliveryCharges: e.target.value }))} />
            </label>
            <label className="field">
              <span>Grand Total</span>
              <input type="number" value={form.grandTotal} onChange={(e) => setForm((p) => ({ ...p, grandTotal: e.target.value }))} />
            </label>
            <label className="field">
              <span>Delivery Date</span>
              <input type="datetime-local" value={form.deliveryDate} onChange={(e) => setForm((p) => ({ ...p, deliveryDate: e.target.value }))} />
            </label>
            <label className="field">
              <span>Remarks</span>
              <textarea rows={2} value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} />
            </label>
            {error && <div className="error-banner">{error}</div>}
            {detailError && activeTab === "order" && <div className="error-banner">{detailError}</div>}
            {renderOrderItemsPanel()}
          </form>
        )}

        {activeTab === "details" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {detailError && <div className="error-banner">{detailError}</div>}
            {renderOrderItemsPanel()}

            <form
              id="order-details-inline-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              {editingId != null && (
                <label className="field">
                  <span>Order ID (header)</span>
                  <input readOnly value={String(editingId)} />
                </label>
              )}
              {detailEditingId != null && (
                <label className="field">
                  <span>Line item ID</span>
                  <input readOnly value={String(detailEditingId)} />
                </label>
              )}
              <label className="field">
                <span>Product</span>
                <select
                  value={detailForm.productId}
                  onChange={(e) =>
                    setDetailForm((p) => ({
                      ...p,
                      productId: e.target.value ? Number(e.target.value) : "",
                      productVarientId: ""
                    }))
                  }
                >
                  <option value="">Select product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || `Product #${p.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Product variant</span>
                <select
                  value={detailForm.productVarientId}
                  onChange={(e) => setDetailForm((p) => ({ ...p, productVarientId: e.target.value ? Number(e.target.value) : "" }))}
                  disabled={!detailForm.productId}
                >
                  <option value="">{detailForm.productId ? "Select variant" : "Select product first"}</option>
                  {varientsForProduct.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.skU || `Variant #${p.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Vendor</span>
                <select
                  value={detailForm.vendorId}
                  onChange={(e) =>
                    setDetailForm((p) => ({
                      ...p,
                      vendorId: e.target.value ? Number(e.target.value) : "",
                      vendorAddressId: ""
                    }))
                  }
                >
                  <option value="">Select vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name || `Vendor #${v.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Vendor address</span>
                <select
                  value={detailForm.vendorAddressId}
                  onChange={(e) => setDetailForm((p) => ({ ...p, vendorAddressId: e.target.value ? Number(e.target.value) : "" }))}
                  disabled={!detailForm.vendorId}
                >
                  <option value="">{detailForm.vendorId ? "Select address" : "Select vendor first"}</option>
                  {vendorAddressesForVendor.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.address ?? `Address #${v.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Unit price</span>
                <input type="number" value={detailForm.unitPrice} onChange={(e) => setDetailForm((p) => ({ ...p, unitPrice: e.target.value }))} />
              </label>
              <label className="field">
                <span>Discount</span>
                <input type="number" value={detailForm.discount} onChange={(e) => setDetailForm((p) => ({ ...p, discount: e.target.value }))} />
              </label>
              <label className="field">
                <span>Taxable</span>
                <input type="number" value={detailForm.taxable} onChange={(e) => setDetailForm((p) => ({ ...p, taxable: e.target.value }))} />
              </label>
              <label className="field">
                <span>Tax amount</span>
                <input type="number" value={detailForm.taxAmount} onChange={(e) => setDetailForm((p) => ({ ...p, taxAmount: e.target.value }))} />
              </label>
              <label className="field">
                <span>Line total</span>
                <input type="number" value={detailForm.grandTotal} onChange={(e) => setDetailForm((p) => ({ ...p, grandTotal: e.target.value }))} />
              </label>
              <label className="field">
                <span>Item status</span>
                <select value={detailForm.itemStatus} onChange={(e) => setDetailForm((p) => ({ ...p, itemStatus: e.target.value }))}>
                  {ORDER_ITEM_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              {(detailEditingId || searchParams.get("detailEdit")) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipDetailHydrateRef.current = true;
                      setDetailEditingId(null);
                      setDetailForm(emptyDetailForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("detailEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New order detail
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
